/**
 * 【custom】検査結果ページ MENU / ガイダンスボタン + モーダル
 *
 * - MENU ボタンで MENU モーダル、ガイダンスボタンでガイダンス画像モーダルを開閉する。
 *   （ESC / オーバーレイ / × で閉じる。フォーカストラップあり）
 * - 開閉ロジックは createModalController に共通化し、`data-result-menu-open="<key>"` と
 *   `data-result-menu-modal="<key>"` の key で開くボタンとモーダルを対応付ける。
 * - ページ内アンカーは「要素 id」または「JSON テンプレートのセクションキー」で指定できる。
 *   セクションキーの場合、Shopify が出力する `shopify-section-template--{id}__{key}` を末尾一致で解決する。
 * - 既存の custom-modal.js と干渉しないよう、スクロールロックは <html> へのクラス付与で行う。
 *   複数モーダルが同時に開いた場合に備え、開いている数をカウントしてクラスを制御する。
 * - テーマエディタの section:load / section:unload に備えて初期化は冪等（idempotent）にし、
 *   unload 時にはリスナーを解除する。
 */
(function () {
  "use strict";

  const SCROLL_VISIBLE_THRESHOLD = 200;
  const LOCK_CLASS = "c-result-menu-lock";
  const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

  /** 開いているモーダル数（複数セクション・複数モーダルで共有） */
  let openModalCount = 0;

  function lockScroll() {
    openModalCount += 1;
    document.documentElement.classList.add(LOCK_CLASS);
  }

  function unlockScroll() {
    openModalCount = Math.max(0, openModalCount - 1);
    if (openModalCount === 0) {
      document.documentElement.classList.remove(LOCK_CLASS);
    }
  }

  /**
   * アンカー文字列から対象要素を解決する。
   * 1. 要素 id に完全一致
   * 2. id が `__{key}` で終わるセクションラッパ
   */
  function resolveAnchorTarget(anchor) {
    if (!anchor) return null;

    const byId = document.getElementById(anchor);
    if (byId) return byId;

    try {
      return document.querySelector('[id$="__' + CSS.escape(anchor) + '"]');
    } catch (error) {
      console.warn("[custom-result-menu] アンカーを解決できませんでした:", anchor, error);
      return null;
    }
  }

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  function scrollToTarget(target) {
    target.style.scrollMarginBlockStart = "calc(var(--sticky-area-height, 0px) + 16px)";
    target.scrollIntoView({
      behavior: prefersReducedMotion() ? "auto" : "smooth",
      block: "start",
    });
  }

  /**
   * 1つのモーダルの開閉・ESC・フォーカストラップを管理する。
   * @returns {{ open: Function, close: Function, isOpen: Function, destroy: Function }}
   */
  function createModalController(options) {
    const modal = options.modal;
    const dialog = options.dialog;
    const openButton = options.openButton;

    let isOpen = false;

    function getFocusableElements() {
      return Array.from(modal.querySelectorAll(FOCUSABLE_SELECTOR)).filter(function (element) {
        return element.offsetParent !== null;
      });
    }

    function open() {
      if (isOpen) return;
      isOpen = true;
      modal.hidden = false;
      modal.classList.add("is-open");
      modal.setAttribute("aria-hidden", "false");
      openButton.setAttribute("aria-expanded", "true");
      lockScroll();

      const focusable = getFocusableElements();
      if (focusable.length > 0) {
        focusable[0].focus();
      }
    }

    function close(closeOptions) {
      if (!isOpen) return;
      isOpen = false;
      modal.classList.remove("is-open");
      modal.setAttribute("aria-hidden", "true");
      modal.hidden = true;
      openButton.setAttribute("aria-expanded", "false");
      unlockScroll();

      const shouldRestoreFocus = !closeOptions || closeOptions.restoreFocus !== false;
      if (shouldRestoreFocus) {
        openButton.focus();
      }
    }

    function onOpenButtonClick() {
      if (isOpen) {
        close();
      } else {
        open();
      }
    }

    function onCloseClick() {
      close();
    }

    // 白いパネルの外側（黒背景部分）のクリックで閉じる
    // ※ overlay の上に中央寄せ用の dialog ラッパが重なるため、dialog 自身へのクリックを拾う
    function onDialogClick(event) {
      if (event.target === dialog) {
        close();
      }
    }

    // ESC とフォーカストラップ（自モーダルが開いているときだけ処理）
    function onKeydown(event) {
      if (!isOpen) return;

      if (event.key === "Escape") {
        event.preventDefault();
        close();
        return;
      }

      if (event.key !== "Tab") return;

      const focusable = getFocusableElements();
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    const closeButtons = Array.from(modal.querySelectorAll("[data-result-menu-close]"));

    openButton.addEventListener("click", onOpenButtonClick);
    closeButtons.forEach(function (element) {
      element.addEventListener("click", onCloseClick);
    });
    if (dialog) {
      dialog.addEventListener("click", onDialogClick);
    }
    modal.addEventListener("keydown", onKeydown);

    function destroy() {
      close({ restoreFocus: false });
      openButton.removeEventListener("click", onOpenButtonClick);
      closeButtons.forEach(function (element) {
        element.removeEventListener("click", onCloseClick);
      });
      if (dialog) {
        dialog.removeEventListener("click", onDialogClick);
      }
      modal.removeEventListener("keydown", onKeydown);
    }

    return {
      open: open,
      close: close,
      isOpen: function () {
        return isOpen;
      },
      destroy: destroy,
    };
  }

  /**
   * MENU モーダル内のページ内アンカーリンクを初期化する。
   */
  function initAnchorLinks(modal, controller) {
    modal.querySelectorAll("[data-result-menu-anchor]").forEach(function (link) {
      link.addEventListener("click", function (event) {
        const anchor = link.dataset.resultMenuAnchor;
        const target = resolveAnchorTarget(anchor);

        if (!target) {
          // 解決できない場合はブラウザ標準の hash 遷移に任せる
          controller.close({ restoreFocus: false });
          return;
        }

        event.preventDefault();
        controller.close({ restoreFocus: false });
        scrollToTarget(target);

        if (target.id && window.history && window.history.replaceState) {
          window.history.replaceState(null, "", "#" + target.id);
        }
      });
    });
  }

  function initResultMenu(root) {
    if (root.dataset.resultMenuReady === "true") return;
    root.dataset.resultMenuReady = "true";

    const nav = root.querySelector("[data-result-menu-nav]");
    if (!nav) {
      console.warn("[custom-result-menu] 必要な要素が見つかりません。");
      return;
    }

    // --- ボタン群の表示切替（スクロール量） ---
    let isTicking = false;
    function updateNavVisibility() {
      const shouldShow = window.scrollY > SCROLL_VISIBLE_THRESHOLD;
      nav.classList.toggle("is-visible", shouldShow);
      isTicking = false;
    }
    function onScroll() {
      if (isTicking) return;
      isTicking = true;
      window.requestAnimationFrame(updateNavVisibility);
    }
    window.addEventListener("scroll", onScroll, { passive: true });
    updateNavVisibility();

    // --- モーダル（key ごとに開くボタンとモーダルを対応付け） ---
    const controllers = [];

    root.querySelectorAll("[data-result-menu-modal]").forEach(function (modal) {
      const key = modal.dataset.resultMenuModal;
      const openButton = root.querySelector('[data-result-menu-open="' + key + '"]');

      if (!openButton) {
        console.warn("[custom-result-menu] モーダルに対応するボタンが見つかりません:", key);
        return;
      }

      const controller = createModalController({
        modal: modal,
        dialog: modal.querySelector("[data-result-menu-dialog]"),
        openButton: openButton,
      });
      controllers.push(controller);

      if (key === "menu") {
        initAnchorLinks(modal, controller);
      }
    });

    if (controllers.length === 0) {
      console.warn("[custom-result-menu] 初期化できるモーダルがありません。");
    }

    // --- テーマエディタでの再描画に備えた後始末 ---
    root.resultMenuDestroy = function () {
      window.removeEventListener("scroll", onScroll);
      controllers.forEach(function (controller) {
        controller.destroy();
      });
      delete root.dataset.resultMenuReady;
    };
  }

  function destroyResultMenu(root) {
    if (typeof root.resultMenuDestroy === "function") {
      root.resultMenuDestroy();
      delete root.resultMenuDestroy;
    }
  }

  function initAll() {
    document.querySelectorAll("[data-result-menu]").forEach(initResultMenu);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }

  // テーマエディタでセクションが再描画されたとき
  document.addEventListener("shopify:section:load", function (event) {
    const root = event.target && event.target.querySelector("[data-result-menu]");
    if (root) initResultMenu(root);
  });

  document.addEventListener("shopify:section:unload", function (event) {
    const root = event.target && event.target.querySelector("[data-result-menu]");
    if (root) destroyResultMenu(root);
  });
})();
